import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Plus, Trash2, ChevronUp, ChevronDown, Eye, EyeOff, Copy } from 'lucide-react';

const newLocation = () => ({
  id: `loc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  group_heading: '',
  name: '',
  address: '',
  phone: '',
  hidden: false,
});

const VisitUsLocationsCard = ({ locations, onChange }) => {
  const list = Array.isArray(locations) ? locations : [];

  const update = (idx, patch) => {
    const next = list.map((loc, i) => (i === idx ? { ...loc, ...patch } : loc));
    onChange(next);
  };

  const remove = (idx) => {
    if (!window.confirm('Remove this location from the footer?')) return;
    onChange(list.filter((_, i) => i !== idx));
  };

  const move = (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= list.length) return;
    const next = list.slice();
    const [item] = next.splice(idx, 1);
    next.splice(to, 0, item);
    onChange(next);
  };

  const duplicate = (idx) => {
    const src = list[idx];
    const clone = { ...src, id: `loc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}` };
    const next = list.slice();
    next.splice(idx + 1, 0, clone);
    onChange(next);
  };

  const add = () => {
    onChange([...list, newLocation()]);
  };

  return (
    <div className="bg-white rounded-lg border p-6" data-testid="visit-us-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-semibold text-[#0A2342] mb-1">Store Locations (Footer "Visit Us")</h3>
          <p className="text-xs text-gray-500">
            Add, edit, reorder or hide the store locations shown at the bottom of every page.
            Group locations under a shared heading (e.g. "For New Gear") by giving them the same heading text.
            Remember to click <span className="font-semibold">Save Settings</span> at the top of the page after making changes.
          </p>
        </div>
        <Button
          type="button"
          onClick={add}
          size="sm"
          className="bg-[#0A2342] hover:bg-[#0A2342]/90 text-white shrink-0"
          data-testid="visit-us-add"
        >
          <Plus className="h-4 w-4 mr-1" /> Add Location
        </Button>
      </div>

      {list.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed rounded-lg text-sm text-gray-500">
          No locations yet. Click <span className="font-semibold">Add Location</span> to create one.
        </div>
      ) : (
        <div className="space-y-4">
          {list.map((loc, idx) => (
            <div
              key={loc.id}
              className={`border rounded-lg p-4 ${loc.hidden ? 'bg-gray-50 opacity-70' : 'bg-white'}`}
              data-testid={`visit-us-item-${idx}`}
            >
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span className="font-semibold text-[#0A2342]">#{idx + 1}</span>
                  {loc.hidden && (
                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-semibold">
                      HIDDEN
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    title="Move up"
                    data-testid={`visit-us-up-${idx}`}
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => move(idx, 1)}
                    disabled={idx === list.length - 1}
                    title="Move down"
                    data-testid={`visit-us-down-${idx}`}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => update(idx, { hidden: !loc.hidden })}
                    title={loc.hidden ? 'Show on site' : 'Hide from site'}
                    data-testid={`visit-us-hide-${idx}`}
                  >
                    {loc.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => duplicate(idx)}
                    title="Duplicate"
                    data-testid={`visit-us-dup-${idx}`}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => remove(idx)}
                    title="Delete"
                    className="text-red-600 hover:bg-red-50"
                    data-testid={`visit-us-delete-${idx}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Group Heading <span className="text-gray-400">(share with next locations to group them)</span>
                  </label>
                  <Input
                    value={loc.group_heading || ''}
                    onChange={(e) => update(idx, { group_heading: e.target.value })}
                    placeholder="e.g. For New Gear"
                    data-testid={`visit-us-heading-${idx}`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Location Name</label>
                  <Input
                    value={loc.name || ''}
                    onChange={(e) => update(idx, { name: e.target.value })}
                    placeholder="e.g. Aqua Divers Pretoria"
                    data-testid={`visit-us-name-${idx}`}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Address <span className="text-gray-400">(one line per row)</span>
                  </label>
                  <textarea
                    className="w-full border rounded-md p-2 text-sm"
                    rows={3}
                    value={loc.address || ''}
                    onChange={(e) => update(idx, { address: e.target.value })}
                    placeholder={'Shop G145 Parkview Shopping Centre\n24 Garsfontein Rd, Pretoria'}
                    data-testid={`visit-us-address-${idx}`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                  <Input
                    value={loc.phone || ''}
                    onChange={(e) => update(idx, { phone: e.target.value })}
                    placeholder="+2712 880 5300"
                    data-testid={`visit-us-phone-${idx}`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VisitUsLocationsCard;
